import React, { useState, useMemo } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { DisplayGroup, NameDisplayFormat } from '../../shared/types.js';
import Button from './Button';
import AssignGroupEmployeesModal from './AssignGroupEmployeesModal';
import AssignGroupManagersModal from './AssignGroupManagersModal';
import { formatEmployeeName } from '../utils.js';

interface Props {
    group: DisplayGroup;
    onUpdate: (group: DisplayGroup) => void;
}

const Fieldset: React.FC<{ legend: string, children: React.ReactNode, className?: string }> = ({ legend, children, className }) => (
    <div className={`border border-gray-300 dark:border-gray-700 rounded-lg p-3 ${className}`}>
        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 -mt-5 px-2 bg-white dark:bg-gray-800 w-fit">{legend}</p>
        <div className="pt-2 space-y-3">{children}</div>
    </div>
);

const DisplayGroupMembersTab: React.FC<Props> = ({ group, onUpdate }) => {
    const { employees } = useTimeClock();
    const [isEmployeesModalOpen, setIsEmployeesModalOpen] = useState(false);
    const [isManagersModalOpen, setIsManagersModalOpen] = useState(false);
    const groupEmployees = useMemo(() => {
        return employees
            .filter(emp => group.employeeIds.includes(emp.id))
            .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
    }, [group.employeeIds, employees]);
    const handleSaveEmployeeAssignments = (newEmployeeIds: string[]) => {
        onUpdate({ ...group, employeeIds: newEmployeeIds });
        setIsEmployeesModalOpen(false);
    };
    const groupManagers = useMemo(() => {
        return employees
            .filter(emp => group.managerIds.includes(emp.id))
            .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
    }, [group.managerIds, employees]);
    const handleSaveManagerAssignments = (newManagerIds: string[]) => {
        onUpdate({ ...group, managerIds: newManagerIds });
        setIsManagersModalOpen(false);
    };
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
                <Fieldset legend="Employees">
                    <div className="h-80 overflow-y-auto border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 rounded-md">
                        {groupEmployees.map(employee => (
                            <div key={employee.id} className="p-2 border-b border-gray-200 dark:border-gray-700/50">{formatEmployeeName(employee, NameDisplayFormat.LAST_FIRST)}</div>
                        ))}
                         {groupEmployees.length === 0 && <p className="p-4 text-center text-gray-500">No employees in this group.</p>}
                    </div>
                    <div className="text-right mt-2">
                        <Button 
                            variant="secondary" 
                            size="sm" 
                            onClick={() => setIsEmployeesModalOpen(true)}
                            title={"Select employees for this group"}
                        >
                            Select Employees...
                        </Button>
                    </div>
                </Fieldset>
            </div>
             <div>
                <Fieldset legend="Managers">
                    <div className="h-80 overflow-y-auto border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 rounded-md">
                        {groupManagers.map(employee => (
                            <div key={employee.id} className="p-2 border-b border-gray-200 dark:border-gray-700/50">{formatEmployeeName(employee, NameDisplayFormat.LAST_FIRST)}</div>
                        ))}
                         {groupManagers.length === 0 && <p className="p-4 text-center text-gray-500">No managers assigned.</p>}
                    </div>
                    <div className="text-right mt-2">
                        <Button variant="secondary" size="sm" onClick={() => setIsManagersModalOpen(true)}>Select Managers...</Button>
                    </div>
                </Fieldset>
            </div>
            <AssignGroupEmployeesModal
                isOpen={isEmployeesModalOpen}
                onClose={() => setIsEmployeesModalOpen(false)}
                onSave={handleSaveEmployeeAssignments}
                group={group}
                employees={employees}
            />
             <AssignGroupManagersModal
                isOpen={isManagersModalOpen}
                onClose={() => setIsManagersModalOpen(false)}
                onSave={handleSaveManagerAssignments}
                group={group}
                employees={employees}
            />
        </div>
    );
};

export default DisplayGroupMembersTab;